import type { PatternPreset } from '~/types/presets';
// Default pattern presets
import { getDefaultFMPattern } from '~/config/presets/patterns/default-fm-pattern';
import { getDefaultMonoPattern } from '~/config/presets/patterns/default-mono-pattern';
import { getDefaultHardwarePattern } from '~/config/presets/patterns/default-hardware-pattern';
// OG pattern presets
import { getOGFMPattern } from '~/config/presets/patterns/og-fm-pattern';
import { getOGMonoPattern } from '~/config/presets/patterns/og-mono-pattern';
import { getOGHardwarePattern } from '~/config/presets/patterns/og-hardware-pattern';

/**
 * PatternPresetManager
 *
 * Manages Pattern presets for all device types.
 * Pattern presets are device-agnostic and can be applied to any device.
 *
 * Provides methods to retrieve, create, and manage pattern presets.
 *
 * Currently supports:
 * - FM Synth Default: Sparse quarter-note pattern in G# Minor Pentatonic
 * - Monosynth Default: Driving eighth-note pattern in D# Minor
 * - Hardware Default: Groovy eighth-note pattern in G# Major Pentatonic
 *
 * Future functionality (when Supabase integration is added):
 * - Load user patterns from database
 * - Save custom patterns
 * - Share patterns publicly
 * - Import/export patterns
 * - Tag patterns by style/genre
 * - Search patterns by characteristics
 */
export class PatternPresetManager {
  private patterns: Map<string, PatternPreset> = new Map();

  constructor() {
    this.initializeSystemPatterns();
  }

  /**
   * Initialize system patterns (built-in defaults)
   */
  private initializeSystemPatterns(): void {
    // Default pattern presets
    const defaultFM = getDefaultFMPattern();
    const defaultMono = getDefaultMonoPattern();
    const defaultHardware = getDefaultHardwarePattern();

    // OG pattern presets
    const ogFM = getOGFMPattern();
    const ogMono = getOGMonoPattern();
    const ogHardware = getOGHardwarePattern();

    // Register all patterns
    this.patterns.set(defaultFM.id, defaultFM);
    this.patterns.set(defaultMono.id, defaultMono);
    this.patterns.set(defaultHardware.id, defaultHardware);

    this.patterns.set(ogFM.id, ogFM);
    this.patterns.set(ogMono.id, ogMono);
    this.patterns.set(ogHardware.id, ogHardware);
  }

  /**
   * Get a pattern preset by ID
   * @param id - Pattern ID
   * @returns PatternPreset or undefined if not found
   */
  getPattern(id: string): PatternPreset | undefined {
    return this.patterns.get(id);
  }

  /**
   * Get all available pattern presets
   * @returns Array of all pattern presets
   */
  getAllPatterns(): PatternPreset[] {
    return Array.from(this.patterns.values());
  }

  /**
   * Add a new pattern preset to the manager
   * @param pattern - Pattern preset to add
   */
  addPattern(pattern: PatternPreset): void {
    this.patterns.set(pattern.id, pattern);
  }

  /**
   * Remove a pattern preset from the manager
   * @param id - Pattern ID to remove
   * @returns true if removed, false if not found
   */
  removePattern(id: string): boolean {
    return this.patterns.delete(id);
  }

  /**
   * Check if a pattern exists
   * @param id - Pattern ID
   * @returns true if pattern exists
   */
  hasPattern(id: string): boolean {
    return this.patterns.has(id);
  }

  /**
   * Get the number of patterns
   * @returns Number of patterns
   */
  getPatternCount(): number {
    return this.patterns.size;
  }

  /**
   * Search patterns by name (case-insensitive)
   * @param query - Search query
   * @returns Array of matching pattern presets
   */
  searchPatterns(query: string): PatternPreset[] {
    const lowerQuery = query.toLowerCase();
    return Array.from(this.patterns.values()).filter(pattern =>
      pattern.name.toLowerCase().includes(lowerQuery)
    );
  }

  // Future methods (for Supabase integration):
  // - async loadUserPatterns(userId: string): Promise<void>
  // - async savePattern(pattern: PatternPreset): Promise<void>
  // - async deletePattern(id: string): Promise<void>
  // - async sharePattern(id: string): Promise<void>
  // - async importPattern(json: string): Promise<PatternPreset>
  // - exportPattern(id: string): string
  // - searchPatternsByCharacteristics(filters: PatternFilters): PatternPreset[]
  // - getPatternsByTag(tag: string): PatternPreset[]
}

// Singleton instance
let patternPresetManagerInstance: PatternPresetManager | null = null;

/**
 * Get the PatternPresetManager singleton instance
 */
export function getPatternPresetManager(): PatternPresetManager {
  if (!patternPresetManagerInstance) {
    patternPresetManagerInstance = new PatternPresetManager();
  }
  return patternPresetManagerInstance;
}

/**
 * Reset the PatternPresetManager singleton (useful for testing)
 */
export function resetPatternPresetManager(): void {
  patternPresetManagerInstance = null;
}
